/**
 * StudioTeamRoles — the studio roles, the RBAC reference matrix and the staff
 * accounts panel, all in one place. Staff sign in with their normal app admin
 * account; the role here grants their studio permissions.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TeamMembersPanel } from '@/components/team/TeamMembersPanel';
import api from '@/lib/api';

export const StudioTeamRoles = () => {
  const { data } = useQuery({
    queryKey: ['fwtv-studio-roles'],
    queryFn: () => api.get('/fwtv/studio/roles').then((r) => r.data),
  });

  const roles = data?.roles || [];
  const matrix = data?.matrix || { columns: [], rows: [] };
  const mine = data?.my_permissions || [];

  return (
    <div className="space-y-8" data-testid="fwtv-studio-team">
      <section>
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
          Studio team roles
        </h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map((r) => (
            <Card key={r.value} data-testid={`studio-role-${r.value}`}>
              <CardContent className="p-5">
                <p className="font-semibold text-sm">{r.label}</p>
                <p className="mt-2 text-xs text-slate-600">{r.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
          Roles &amp; permissions
        </h3>
        <p className="mt-2 text-xs text-slate-600" data-testid="studio-my-access">
          Your effective access:{' '}
          {data?.is_super ? 'super admin — all permissions'
            : mine.length ? mine.join(', ') : 'no studio role assigned yet'}
        </p>
        <div className="mt-4 overflow-x-auto border rounded-md">
          <table className="w-full text-xs" data-testid="studio-permission-matrix">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Role</th>
                {matrix.columns.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {matrix.rows.map((row) => (
                <tr key={row.role} data-testid={`matrix-row-${row.role}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{row.label}</td>
                  {matrix.columns.map((c) => (
                    <td key={c} className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {row.cells[c] === 'All'
                        ? <Badge className="bg-slate-900 text-white hover:bg-slate-900">All</Badge>
                        : row.cells[c]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Roles are enforced by the server API on every studio endpoint.
        </p>
      </section>

      <section data-testid="studio-staff-accounts">
        <TeamMembersPanel
          apiPath="fwtv"
          roles={roles}
          title="Staff accounts"
          description="Studio staff use their normal app sign-in; the role below grants their production permissions."
        />
      </section>
    </div>
  );
};

export default StudioTeamRoles;
